import React from 'react';
import { Activity } from 'lucide-react';
import type { CadeiaExecutiveImpact } from '../../services/cadeiaChainInsights';
import { GRAVIDADE_LABEL, GRAVIDADE_STYLE } from '../../services/cadeiaSeverityStyles';

interface CadeiaExecutiveImpactCardProps {
  impact: CadeiaExecutiveImpact;
}

/** Bloco compacto — impacto observado do líder mais crítico. */
export const CadeiaExecutiveImpactCard: React.FC<CadeiaExecutiveImpactCardProps> = ({ impact }) => {
  const style = GRAVIDADE_STYLE[impact.gravidade];

  return (
    <section className={`rounded-lg border px-3 py-2 ${style.border} bg-white/80 dark:bg-slate-950/30`}>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <p className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide text-slate-500">
          <Activity size={11} className="text-[#2F6FED]" />
          Líder mais crítico
        </p>
        <span className={`rounded px-1.5 py-0.5 text-[8px] font-bold uppercase ${style.badge}`}>
          {GRAVIDADE_LABEL[impact.gravidade]}
        </span>
      </div>

      <p className="mb-2 truncate text-[13px] font-bold text-slate-800 dark:text-slate-100">
        {impact.leaderName}
      </p>

      {impact.operationalMode ? (
        <div className="grid grid-cols-3 gap-1.5">
          <ImpactMetric
            label="Aderência"
            value={`${impact.aderenciaPct}%`}
            tone={impact.gravidade}
            highlight={impact.aderenciaPct < 80}
          />
          <ImpactMetric
            label="Não realizadas"
            value={String(impact.rotinasNaoRealizadas)}
            tone={impact.gravidade}
            highlight={impact.rotinasNaoRealizadas > 0}
          />
          <ImpactMetric
            label="Pior dia"
            value={impact.piorDiaLabel ?? '—'}
            tone={impact.gravidade}
            small
            highlight={!!impact.piorDiaLabel}
          />
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-1">
          {impact.rotinasNaoRealizadas > 0 && (
            <ImpactMetric
              label="Não realizadas"
              value={String(impact.rotinasNaoRealizadas)}
              tone={impact.gravidade}
              highlight
            />
          )}
          <ImpactMetric
            label="Aderência"
            value={`${impact.aderenciaPct}%`}
            tone={impact.gravidade}
            highlight={impact.aderenciaPct < 80}
          />
          {impact.desvios > 0 && (
            <ImpactMetric label="Desvios" value={String(impact.desvios)} tone={impact.gravidade} highlight />
          )}
          {impact.perdidas > 0 && (
            <ImpactMetric label="Perdas" value={String(impact.perdidas)} tone={impact.gravidade} highlight />
          )}
        </div>
      )}
    </section>
  );
};

function ImpactMetric({
  label,
  value,
  tone,
  highlight = false,
  small = false,
}: {
  label: string;
  value: string;
  tone: CadeiaExecutiveImpact['gravidade'];
  highlight?: boolean;
  small?: boolean;
}) {
  const valueCls = highlight
    ? tone === 'critico'
      ? 'text-red-600 dark:text-red-400'
      : tone === 'atencao'
        ? 'text-amber-700 dark:text-amber-400'
        : 'text-slate-800 dark:text-slate-100'
    : 'text-slate-800 dark:text-slate-100';

  return (
    <div className="rounded border border-slate-100 bg-slate-50/80 px-1.5 py-1 dark:border-slate-800 dark:bg-slate-900/40">
      <p className="text-[8px] uppercase text-slate-400">{label}</p>
      <p
        className={`font-bold tabular-nums ${valueCls} ${small ? 'truncate text-[9px] leading-tight' : 'text-[12px]'}`}
        title={small ? value : undefined}
      >
        {value}
      </p>
    </div>
  );
}

export default CadeiaExecutiveImpactCard;
